import * as moment from "moment";
import {toBoolean, toList, toNumber, toString} from "./helpers/helpers";
import {get} from "lodash";
import {KeyByObject} from "./misc/reduce-helpers";

export interface JSONModelConstructor {
  new (data: any): JSONModel;
}

/* eslint-disable */
export type AutoPropertyAction = "date" | "json" | "string" | "number" | "boolean" | "stringDate" |
  "number0" | "booleanTrue" | "json{}" | "flags" | "any" |
  "JSON.stringify";
/* eslint-enable */

export type AutoPropertyFunction = (value?: any, data?: any, instance?: JSONModel) => any;
export type AutoPropertyProcessor = AutoPropertyAction | boolean | AutoPropertyFunction | JSONModelConstructor;
export type JSONPropertyAction = AutoPropertyProcessor | [AutoPropertyProcessor] | [AutoPropertyAction | string];
export type JSONPropertyType = {src: string, dest: string, action: JSONPropertyAction};

function processDate(value: any): Date {
  if (value === null || value === undefined) return undefined;

  let date;

  if (value && typeof value === "object" && value instanceof Date) {
    return value;
  }

  if (typeof value === "number" || /^[0-9]+$/.test(`${value}`)) {
    date = moment.utc(+value);
  } else if (typeof value === "string") {
    date = moment.utc(value);
  }

  if (date.isValid()) {
    return date.toDate();
  }

  console.warn(`Date value ${value} is not valid.`);

  return undefined;
}

function processStringDate(value: any) {
  if (typeof value === "string" && /^[0-9]{4}-[0-9]{2}-[0-9]{2}/.test(value)) {
    return value;
  }

  const dateValue = processDate(value);
  if (dateValue !== undefined) {
    return moment.utc(dateValue).format("YYYY-MM-DD");
  }

  return undefined;
}

function makeJson(value: any): string {
  if (value === undefined) return "undefined";
  return JSON.stringify(value);
}

function processJson(value: any, defaultValue = undefined): any {
  if (value === undefined) {
    return defaultValue;
  }

  if (value === "undefined") {
    return undefined;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn(error);
  }

  return defaultValue;
}

function processFlags(value: any): {[key: string]: boolean} {
  if (!value) return {};
  if (typeof value !== "object") return {};
  if (!Array.isArray(value)) return processFlags(Object.keys(value));

  return Array.prototype.map.call(value, toString)
    .reduce((obj, key) => obj.add(key, true), new KeyByObject<boolean>())
    .toObject();
}

export interface JSONModelMeta {
  indexProperty?: string;
  indexMapper?: <T>(index: number) => T;
  jsonProperties?: JSONPropertyType[];
}

export class JSONModel {
  private __meta__: JSONModelMeta;

  private static setJSONProperty(obj: JSONModel, dest: string, src: string, action: JSONPropertyAction) {
    if (!obj.__meta__) obj.__meta__ = {};
    if (!obj.__meta__.jsonProperties) obj.__meta__.jsonProperties = [];
    obj.__meta__.jsonProperties.push({src, dest, action});
  }

  private static setJSONMeta(obj: JSONModel, property: keyof JSONModelMeta, value: any): void {
    if (!obj.__meta__) obj.__meta__ = {};
    obj.__meta__[property] = value;
  }

  static setIndex(item: JSONModel, index: number) {
    const indexProperty: string = item.__meta__.indexProperty;
    const indexMapper: (index: number) => any = item.__meta__.indexMapper;

    if (indexProperty) {
      item[indexProperty] = indexMapper ? indexMapper(index) : index;
    }
  }

  static reIndexList(list: JSONModel[]) {
    for (let index = 0; index < list.length; index++) {
      JSONModel.setIndex(list[index], index);
    }
  }

  /**
   * In a class that extends JSONModel, load data into its properties during construction.
   * @param {JSONPropertyAction} [action] (default blind copy of data with no manipulation)
   * @param {string} [key] (default same as property name)
   */
  static jsonProperty(action: JSONPropertyAction = true, key?: string): any {
    return function jsonPropertyDecorator(target: any, propertyKey: string, descriptor: PropertyDescriptor): any {
      if (descriptor === undefined) {
        descriptor = Object.getOwnPropertyDescriptor(target, propertyKey);
      }

      if (target instanceof JSONModel) {
        JSONModel.setJSONProperty(target, propertyKey, key || propertyKey, action);
      }

      return descriptor;
    };
  }

  /**
   * When getting a list of JSONModels it may be useful to add the index to each one.
   * @param {(index: number) => T} [indexMapper] - optional function to translate numeric index to the data you want
   */
  static jsonListIndex<T>(indexMapper?: (index: number) => T): any {
    return function jsonListIndexDecorator(
      target: any,
      propertyKey: string,
      descriptor: TypedPropertyDescriptor<T>,
    ): TypedPropertyDescriptor<T> {
      if (descriptor === undefined) {
        descriptor = Object.getOwnPropertyDescriptor(target, propertyKey);
      }

      if (target instanceof JSONModel) {
        JSONModel.setJSONMeta(target, "indexProperty", propertyKey);
        if (indexMapper) JSONModel.setJSONMeta(target, "indexMapper", indexMapper);
      }

      return descriptor;
    };
  }

  constructor(data?: any) {
    data = data || {};

    if (!this.__meta__) {
      this.__meta__ = {};
    }

    if (!this.__meta__.jsonProperties) {
      this.__meta__.jsonProperties = [];
    }

    for (const entry of this.__meta__.jsonProperties) {
      const src = entry.src;
      const dest = entry.dest;
      const isArray = entry.action instanceof Array;
      const action: AutoPropertyProcessor = isArray ? entry.action[0] : entry.action;
      let delegate: (v) => any;
      let isJSONModel = false;

      if (action === "date") {
        delegate = value => processDate(value);
      } else if (action === "stringDate") {
        delegate = value => processStringDate(value);
      } else if (action === "json") {
        delegate = value => processJson(value);
      } else if (action === "flags") {
        delegate = value => processFlags(value);
      } else if (action === "json{}") {
        delegate = value => processJson(value, {});
      } else if (action === "JSON.stringify") {
        delegate = value => makeJson(value);
      } else if (action && action["prototype"] && action["prototype"] instanceof JSONModel) {
        isJSONModel = true;
        delegate = value => new (action as JSONModelConstructor)(value);
      } else if (typeof action === "function") {
        delegate = value => (action as AutoPropertyFunction)(value, data, this);
      } else if (action === "string") {
        delegate = toString;
      } else if (action === "boolean") {
        delegate = toBoolean;
      } else if (action === "booleanTrue") {
        delegate = value => toBoolean(value, true);
      } else if (action === "number") {
        delegate = toNumber;
      } else if (action === "number0") {
        delegate = value => toNumber(value, 0);
      } else if (action === true || action === "any") {
        delegate = value => value;
      }

      if (delegate) {
        const value = get(data, src);

        if (isArray) {
          const list = toList(delegate, value);

          if (isJSONModel) {
            for (let index = 0; index < list.length; index++) {
              const item = list[index] as JSONModel;

              JSONModel.setIndex(item, index);
            }
          }

          this[dest] = list;
        } else {
          this[dest] = delegate(value);
        }
      }
    }
  }
}

export const jsonProperty = JSONModel.jsonProperty;
export const jsonListIndex = JSONModel.jsonListIndex;
